import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Flex, FormControl, FormLabel, HStack, IconButton, Input, Select, SimpleGrid,
  Switch, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, VStack, useDisclosure, useToast, Tooltip,
} from '@chakra-ui/react'
import { Calculator, Layers, Pencil, Percent, Play, Plus, Power, Settings2 } from 'lucide-react'
import { operatorsAdmin, type CommissionRule } from '@services/operators'
import { apiErrorMessage, formatDate, formatNumber, formatTzs } from '@utils/format'
import { ConfirmModal, EmptyState, Label, PageHeader, Panel, Pill, SkeletonRows, tableHead } from '@components/ui'
import CommissionRuleModal from '@components/CommissionRuleModal'

function scopeOf(r: CommissionRule) {
  if (r.daladalaId) return { rank: 4, label: 'One bus', detail: `${r.registrationNumber ?? ''}${r.operator ? ` · ${r.operator.name}` : ''}`, tone: 'purple' as const }
  if (r.operator && r.routeId) return { rank: 3, label: 'Company + route', detail: `${r.operator.name} · ${r.routeName ?? ''}`, tone: 'blue' as const }
  if (r.operator) return { rank: 2, label: 'Company', detail: r.operator.name, tone: 'brand' as const }
  if (r.routeId) return { rank: 1, label: 'Route', detail: r.routeName ?? '', tone: 'orange' as const }
  return { rank: 0, label: 'Platform default', detail: 'Everyone without a more specific rule', tone: 'gray' as const }
}

function rateText(r: CommissionRule) {
  const parts: string[] = []
  if (r.percentage) parts.push(`${r.percentage}%`)
  if (r.fixedAmount) parts.push(`${r.percentage ? '+ ' : ''}${formatNumber(r.fixedAmount)} TZS`)
  if (!parts.length) parts.push('0%')
  return parts.join(' ')
}

function SettlementControl() {
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading } = useQuery('settlement-config', () => operatorsAdmin.settlement().then(r => r.data))
  const [enabled, setEnabled] = useState(false)
  const [startAt, setStartAt] = useState('')
  const [confirm, setConfirm] = useState(false)
  useEffect(() => { if (data) { setEnabled(data.enabled); setStartAt(data.startAt ? data.startAt.slice(0, 10) : '') } }, [data])

  const save = useMutation(() => operatorsAdmin.saveSettlement({ enabled, startAt: startAt ? new Date(`${startAt}T00:00:00+03:00`).toISOString() : null }), {
    onSuccess: () => { toast({ title: 'Settlement settings saved', status: 'success' }); qc.invalidateQueries('settlement-config'); qc.invalidateQueries('operators-overview'); setConfirm(false) },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  const run = useMutation(() => operatorsAdmin.runSettlement(), {
    onSuccess: r => {
      const d = r.data
      toast({ title: d.skipped ? 'Settlement is switched off' : `Settled ${d.earnings} ride${d.earnings === 1 ? '' : 's'}, ${d.refunds} refund${d.refunds === 1 ? '' : 's'}`,
        description: d.skippedNoRule ? `${d.skippedNoRule} ride(s) skipped: no commission rule applies.` : undefined, status: d.skippedNoRule || d.errors ? 'warning' : 'success' })
      qc.invalidateQueries('operators-overview')
    },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  const dirty = data ? enabled !== data.enabled || (startAt || '') !== (data.startAt ? data.startAt.slice(0, 10) : '') : false

  return (
    <Panel title="Settlement" subtitle="Credits operators for paid, completed rides. Nothing is credited while this is off.">
      <Flex direction={{ base: 'column', lg: 'row' }} gap={6} align={{ lg: 'flex-end' }} justify="space-between">
        <HStack spacing={8} flexWrap="wrap" align="flex-end">
          <FormControl w="auto"><FormLabel fontSize="sm" mb={2}>Settlement</FormLabel>
            <HStack><Switch size="lg" colorScheme="green" isChecked={enabled} onChange={e => setEnabled(e.target.checked)} isDisabled={isLoading} />
              <Text fontSize="sm" fontWeight="medium" color={enabled ? 'brand.700' : 'gray.500'}>{enabled ? 'On' : 'Off'}</Text></HStack></FormControl>
          <FormControl w="220px"><FormLabel fontSize="sm">Count rides completed from</FormLabel>
            <Input type="date" value={startAt} onChange={e => setStartAt(e.target.value)} /></FormControl>
        </HStack>
        <HStack>
          <Button variant="outline" leftIcon={<Play size={15} />} isLoading={run.isLoading} onClick={() => run.mutate()} isDisabled={!data?.enabled}>Run now</Button>
          <Button isDisabled={!dirty || (enabled && !startAt)} onClick={() => (enabled && !data?.enabled ? setConfirm(true) : save.mutate())} isLoading={save.isLoading}>Save</Button>
        </HStack>
      </Flex>
      <Text mt={4} fontSize="xs" color="gray.500">Rides completed before the start date are never credited, so switching on does not back-pay old trips. Settlement also runs automatically every 10 minutes.</Text>
      <ConfirmModal isOpen={confirm} onClose={() => setConfirm(false)} title="Switch on settlement?" confirmLabel="Switch on" loading={save.isLoading} onConfirm={() => save.mutate()}
        body={<>Operators will start being credited for completed, paid rides from <b>{startAt}</b>, using the commission rules below. Check that your platform default is set.</>} />
    </Panel>
  )
}

function TestRide({ rules }: { rules: CommissionRule[] }) {
  const toast = useToast()
  const { data: ops } = useQuery('rule-operators', () => operatorsAdmin.list({ limit: 100 }), { staleTime: 60_000 })
  const [operatorId, setOperatorId] = useState('')
  const [fare, setFare] = useState('9620')
  const test = useMutation(() => operatorsAdmin.preview({ operatorId, fare: Number(fare) }), { onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) } })
  const r = test.data?.data
  return (
    <Panel title="Check a company" subtitle="See exactly what a company would be charged on a ride.">
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} alignItems="flex-end">
        <FormControl><FormLabel fontSize="sm">Company</FormLabel>
          <Select value={operatorId} placeholder="Choose…" onChange={e => setOperatorId(e.target.value)}>{(ops?.data ?? []).filter(o => !o.isHouse).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</Select></FormControl>
        <FormControl><FormLabel fontSize="sm">Fare (TZS)</FormLabel><Input type="number" value={fare} onChange={e => setFare(e.target.value)} /></FormControl>
        <Button leftIcon={<Calculator size={15} />} variant="outline" isDisabled={!operatorId} isLoading={test.isLoading} onClick={() => test.mutate()}>Calculate</Button>
      </SimpleGrid>
      {r && (
        <Flex mt={4} p={4} borderRadius="xl" bg={r.settles ? 'brand.50' : 'orange.50'} gap={8} flexWrap="wrap" align="center">
          {r.settles ? (<>
            <Box><Label>Commission</Label><Text fontSize="xl" fontWeight="semibold">{formatTzs(r.commission)}</Text></Box>
            <Box><Label>Company earns</Label><Text fontSize="xl" fontWeight="semibold" color="brand.700">{formatTzs(r.operatorGets)}</Text></Box>
            <Text fontSize="sm" color="gray.600" flex="1">Rule used: {r.basis}</Text>
          </>) : <Text fontSize="sm" color="orange.800">No rule applies to this company, so its rides will not settle. Add a platform default.</Text>}
        </Flex>
      )}
    </Panel>
  )
}

export default function CommissionPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const modal = useDisclosure()
  const [editing, setEditing] = useState<CommissionRule | null>(null)
  const [retiring, setRetiring] = useState<CommissionRule | null>(null)
  const [showInactive, setShowInactive] = useState(false)
  const { data, isLoading } = useQuery('commission-rules', () => operatorsAdmin.rules().then(r => r.data))
  const retire = useMutation((id: string) => operatorsAdmin.retireRule(id), {
    onSuccess: () => { toast({ title: 'Rule switched off', status: 'success' }); qc.invalidateQueries('commission-rules'); setRetiring(null) },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  const rules = (data ?? []).filter(r => showInactive || r.active).sort((a, b) => scopeOf(b).rank - scopeOf(a).rank || b.priority - a.priority)
  const hasDefault = (data ?? []).some(r => r.active && scopeOf(r).rank === 0)

  const open = (r: CommissionRule | null) => { setEditing(r); modal.onOpen() }

  return (
    <>
      <PageHeader eyebrow="Operators" title="Commission & settlement"
        subtitle="Set what Daladala Smart keeps from each fare, globally or per company, route or bus."
        actions={<Button leftIcon={<Plus size={16} />} onClick={() => open(null)}>New rule</Button>} />

      <VStack spacing={6} align="stretch">
        {!isLoading && !hasDefault && (
          <Alert status="warning" borderRadius="xl" alignItems="flex-start"><AlertIcon mt={0.5} />
            <AlertDescription fontSize="sm"><b>No platform default.</b> Rides for any company without its own rule will <b>not</b> settle until you add one. We never guess a rate. <Button size="xs" ml={2} onClick={() => open(null)}>Add default</Button></AlertDescription></Alert>
        )}
        <SettlementControl />

        <Panel flush title="Commission rules" subtitle="The most specific active rule wins." actions={
          <HStack spacing={2}><Text fontSize="sm" color="gray.500">Show switched-off</Text><Switch size="sm" colorScheme="green" isChecked={showInactive} onChange={e => setShowInactive(e.target.checked)} /></HStack>}>
          {isLoading ? <SkeletonRows /> : rules.length === 0 ? (
            <EmptyState icon={<Percent size={26} />} title="No commission rules" body="Create a platform default first. Then add exceptions for specific companies, routes or buses." action={<Button onClick={() => open(null)}>Create platform default</Button>} />
          ) : (
            <TableContainer>
              <Table>
                <Thead><Tr><Th {...tableHead}>Applies to</Th><Th {...tableHead}>Rate</Th><Th {...tableHead}>Limits</Th><Th {...tableHead}>Active</Th><Th {...tableHead} /></Tr></Thead>
                <Tbody>
                  {rules.map(r => {
                    const sc = scopeOf(r)
                    return (
                      <Tr key={r.id} opacity={r.active ? 1 : 0.55} _hover={{ bg: 'gray.50' }}>
                        <Td borderColor="gray.100"><HStack spacing={3} align="flex-start"><Pill tone={sc.tone} dot={false}>{sc.label}</Pill>
                          <Box><Text fontSize="sm" fontWeight="medium">{sc.detail || r.name}</Text>{r.notes && <Text fontSize="xs" color="gray.500" noOfLines={1}>{r.notes}</Text>}</Box></HStack></Td>
                        <Td borderColor="gray.100"><Text fontWeight="semibold" fontSize="lg" letterSpacing="-0.01em">{rateText(r)}</Text></Td>
                        <Td borderColor="gray.100"><Text fontSize="sm" color="gray.600">
                          {r.minAmount != null || r.maxAmount != null ? `${r.minAmount != null ? `min ${formatNumber(r.minAmount)}` : ''}${r.minAmount != null && r.maxAmount != null ? ' · ' : ''}${r.maxAmount != null ? `max ${formatNumber(r.maxAmount)}` : ''}` : '—'}</Text>
                          <Text fontSize="xs" color="gray.400">{r.effectiveTo ? `until ${formatDate(r.effectiveTo)}` : `since ${formatDate(r.effectiveFrom)}`}{r.priority ? ` · priority ${r.priority}` : ''}</Text></Td>
                        <Td borderColor="gray.100">{r.active ? <Pill tone="brand">Active</Pill> : <Pill tone="gray">Off</Pill>}</Td>
                        <Td borderColor="gray.100" isNumeric><HStack justify="flex-end" spacing={1}>
                          <Tooltip label="Edit" hasArrow><IconButton aria-label="Edit" size="sm" variant="ghost" icon={<Pencil size={15} />} onClick={() => open(r)} /></Tooltip>
                          {r.active && <Tooltip label="Switch off" hasArrow><IconButton aria-label="Switch off" size="sm" variant="ghost" color="red.500" icon={<Power size={15} />} onClick={() => setRetiring(r)} /></Tooltip>}
                        </HStack></Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Panel>

        <TestRide rules={data ?? []} />
        <Flex gap={3} p={4} bg="gray.50" borderRadius="xl" color="gray.600" fontSize="sm" align="flex-start"><Box mt={0.5}><Layers size={16} /></Box>
          <Text>Rules are never deleted: switching one off keeps the history of what rate applied when. Every change is recorded in the audit trail with who made it.</Text></Flex>
      </VStack>

      <CommissionRuleModal isOpen={modal.isOpen} onClose={modal.onClose} rule={editing} />
      <ConfirmModal isOpen={!!retiring} onClose={() => setRetiring(null)} title="Switch this rule off?" tone="red" confirmLabel="Switch off" loading={retire.isLoading}
        body="Rides settled from now on fall back to the next most specific rule. Rides already settled are not changed." onConfirm={() => retiring && retire.mutate(retiring.id)} />
    </>
  )
}
